import { groupBy } from 'es-toolkit';
import { filter } from 'es-toolkit/compat';
import { action, computed, makeObservable, observable } from 'mobx';
import { Notification, NotificationLocationCode, NotificationRequest } from '../../models';
import { notificationRequestSchema } from '../../models/schemas/notification-request.schema';
import { createValidationState, ValidationState } from '../../shared/validation';

export class NotificationsPageUiStore {
  @observable notifications: Notification[] = [];

  @observable notificationRequestBody?: NotificationRequest;

  @observable selectedNotification?: Notification;

  @observable selectedNotificationLocationCode?: NotificationLocationCode;

  @observable isErrorToastOpen = false;

  constructor() {
    makeObservable(this);
  }

  @action
  resetStates(): void {
    this.notifications = [];
    this.isErrorToastOpen = false;
  }

  @action
  resetModalStates(): void {
    this.notificationRequestBody = undefined;
    this.selectedNotification = undefined;
    this.selectedNotificationLocationCode = undefined;
  }

  @action
  setNotifications(notifications: Notification[]): void {
    this.notifications = notifications;
  }

  @action
  setIsErrorToastOpen(isOpen: boolean): void {
    this.isErrorToastOpen = isOpen;
  }

  @action
  setSelectedNotificationLocationCode(locationCode: NotificationLocationCode): void {
    this.selectedNotificationLocationCode = locationCode;
  }

  @action
  setSelectedNotification(notification?: Notification): void {
    this.selectedNotification = notification;
  }

  @action
  createNotificationRequestBody(): void {
    const initialValue: NotificationRequest = {
      message: '',
      typeCode: 'info',
      locationCode: 'partner-homepage',
    };

    this.notificationRequestBody = initialValue;
  }

  @action
  createNotificationRequestBodyForUpdate(existingNotification: Notification): void {
    const typeCode = existingNotification.typeCode === 'success' ? 'info' : existingNotification.typeCode;

    const initialValue: NotificationRequest = {
      message: existingNotification.message,
      typeCode: typeCode,
      locationCode: existingNotification.locationCode,
    };

    this.notificationRequestBody = initialValue;
  }

  @action
  setNotificationRequestBody<K extends keyof NotificationRequest>(updatedField: { key: K; value: NotificationRequest[K] }) {
    if (!this.notificationRequestBody) return;

    const { key, value } = updatedField;
    const updatedNotificationRequestBody = { ...this.notificationRequestBody, [key]: value };
    this.notificationRequestBody = updatedNotificationRequestBody;
  }

  @computed
  get notificationsByLocation() {
    return (locationCode: NotificationLocationCode): Notification[] => {
      const grouppedNotifications = groupBy(this.notifications, (notification) => notification.locationCode);
      return grouppedNotifications[locationCode] ?? [];
    };
  }

  @computed
  get canActivateMoreNotifications() {
    return (locationCode?: NotificationLocationCode): boolean => {
      if (!locationCode) return false;
      return filter(this.notificationsByLocation(locationCode), { isActive: true }).length < 2;
    };
  }

  @computed
  get notificationRequestValidation(): ValidationState<NotificationRequest> {
    return createValidationState(
      notificationRequestSchema.safeParse(this.notificationRequestBody),
    );
  }
}
